import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function ONas() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'O nás | CzechAI - Váš partner pro AI transformaci';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Jsme CzechAI - váš partner pro AI transformaci. Od roku 2022 pomáháme firmám digitalizovat a automatizovat. Vlastní AI stack Aurum AI s českým jazykovým modelem.');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "O nás - CzechAI",
      "description": "Informace o společnosti CzechAI a našem týmu",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/o-nas`,
      "mainEntity": {
        "@type": "Organization",
        "name": "CzechAI",
        "foundingDate": "2022",
        "description": "Společnost specializující se na AI řešení pro český byznys",
        "numberOfEmployees": "10-50",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CZ"
        },
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "telephone": "+420-732-941-900",
            "contactType": "customer service",
            "areaServed": "CZ"
          },
          {
            "@type": "ContactPoint", 
            "telephone": "+420-704-105-520",
            "contactType": "customer service",
            "areaServed": "Praha"
          },
          {
            "@type": "ContactPoint",
            "telephone": "+420-775-993-829", 
            "contactType": "customer service",
            "areaServed": "Brno"
          }
        ],
        "hasCredential": [
          "AWS Certified Solutions Architect",
          "Microsoft Azure AI Engineer", 
          "Google Cloud Professional ML Engineer",
          "ISO 27001 Information Security",
          "GDPR Compliance Specialist"
        ]
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Domů",
            "item": import.meta.env.VITE_SITE_URL || "https://example.com"
          },
          {
            "@type": "ListItem",
            "position": 2, 
            "name": "O nás"
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Inovace',
      description: 'Využíváme nejnovější AI technologie a přístupy k řešení business problémů'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Důvěryhodnost',
      description: 'Transparentní komunikace, dodržování termínů a garance výsledků'
    },
    {
      icon: 'ri-lock-line',
      title: 'Bezpečnost',
      description: 'On-premise řešení, GDPR compliance a ochrana vašich citlivých dat'
    },
    {
      icon: 'ri-handshake-line',
      title: 'Partnerství',
      description: 'Stavíme dlouhodobé vztahy založené na vzájemné důvěře a úspěchu'
    },
    {
      icon: 'ri-target-line',
      title: 'Výsledky',
      description: 'Zaměřujeme se na měřitelné business výsledky, ne jen na technologie'
    }
  ];

  const certifications = [
    'AWS Certified Solutions Architect',
    'Microsoft Azure AI Engineer',
    'Google Cloud Professional ML Engineer',
    'ISO 27001 Information Security',
    'GDPR Compliance Specialist'
  ];

  const integrations = [
    'Salesforce', 'HubSpot', 'SAP', 'Odoo', 'POHODA', 'Google Workspace',
    'Microsoft 365', 'PostgreSQL', 'MySQL', 'MS SQL Server', 'Power BI',
    'Looker Studio', 'Slack', 'Microsoft Teams', 'WhatsApp Business API',
    'AWS (S3/Lambda)', 'Microsoft Azure', 'Google Cloud Platform',
    'UiPath (RPA)', 'Make (Integromat)', 'Zapier', 'Shoptet'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero sekce */}
      <section className="relative py-32 bg-black min-h-[80vh] flex items-center">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/559fffeca1b27072ed35cddb956c89af.jfif"
            alt="AI Technology Network"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Jsme CzechAI – váš partner pro AI transformaci
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Od roku 2022 pomáháme firmám digitalizovat a automatizovat. V roce 2025 jsme AI vyčlenili do samostatné firmy CzechAI, 
              abychom doručovali rychleji a s měřitelnými výsledky.
            </p>
          </div>
        </div>
      </section>

      {/* O společnosti */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Naše mise
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Zpřístupnit pokročilé AI technologie firmám všech velikostí a proměnit je v konkrétní dopady: 
                úspora času, nižší náklady, nový růst. Dodáváme řešení, ne sliby – rychlé piloty (2–4 týdny), 
                jasné KPI, transparentní komunikace.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-black mb-2">150+</div>
                  <div className="text-gray-600">Úspěšných projektů</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-black mb-2">3</div>
                  <div className="text-gray-600">Roky zkušeností</div>
                </div>
              </div>
            </div>
            
            <div>
              <img
                src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/45974d0cbddb7159accb1c0d6a1998fa.jfif"
                alt="CzechAI office"
                className="w-full h-96 object-cover object-top rounded-2xl border border-gray-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Aurum AI */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Naše vlastní umělá inteligence – Aurum AI
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Aurum AI je náš proprietární AI stack s českým jazykovým modelem, agentním orchestrem a bezpečným RAG nad vašimi daty.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-lock-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Nasazení on-prem / private cloud</h3>
              <p className="text-gray-600 leading-relaxed">
                Data zůstávají u vás. Plná kontrola nad citlivými informacemi.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Governance & audit</h3>
              <p className="text-gray-600 leading-relaxed">
                Řízení přístupů, logování dotazů, verze promptů.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 text-center border border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-stack-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-bold text-black mb-4">Modulární architektura</h3>
              <p className="text-gray-600 leading-relaxed">
                Od chatbotu přes automatizaci procesu až po prediktivní analýzy.
              </p>
            </div>
          </div>

          {/* Integrace */}
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <h3 className="text-2xl font-bold text-black mb-6 text-center">
              Napojení (výběr 22 integrací):
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {integrations.map((integration, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="w-2 h-2 bg-black rounded-full mr-3"></div>
                  <span className="text-gray-700 text-sm font-medium">{integration}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Hodnoty */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Naše hodnoty
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Principy, které nás vedou při práci s našimi klienty
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dotační expert sekce */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 border-2 border-black flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20humanoid%20robot%20consultant%20working%20on%20modern%20laptop%20computer%2C%20advanced%20AI%20assistant%20providing%20business%20advice%2C%20sleek%20metallic%20design%2C%20modern%20office%20environment%2C%20business%20consulting%20scene%2C%20high-tech%20professional%20setting%2C%20clean%20white%20background%2C%20minimalist%20style%2C%20futuristic%20business%20advisor&width=400&height=300&seq=dotacni-expert-robot&orientation=landscape"
                  alt="AI Dotační expert"
                  className="w-full h-48 object-cover object-top rounded-xl"
                />
              </div>
              <div className="md:w-2/3">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-funds-line text-2xl text-black"></i>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-black mb-4">
                  Dotační expert - konzultant
                </h2>
                <div className="inline-block bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-medium mb-4 border border-gray-200">
                  <i className="ri-time-line mr-2"></i>
                  Připravuje se
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Po stanovení postupu implementace AI řešení pro vaši firmu se na našeho dotačního experta můžete obrátit s dotazy k aktuálním dotačním programům. Pomůžeme vám najít vhodné možnosti financování vašich AI projektů.
                </p>
                <p className="text-gray-600 text-sm">
                  Sledujte naše novinky pro informace o spuštění této služby.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifikace a partnerství */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Certifikace a partnerství
            </h2>
            <p className="text-xl text-gray-600">
              Pracujeme s předními technologickými partnery a držíme aktuální certifikace
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-black mb-6">Certifikace týmu</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/c06522e9d89b0dcc840760ba85d8656b.png"
                    alt="AWS Certification"
                    className="w-10 h-10 object-contain mr-4"
                  />
                  <span className="text-gray-700 font-medium">AWS Certified Solutions Architect</span>
                </div>
                
                <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/c06522e9d89b0dcc840760ba85d8656b.png"
                    alt="Microsoft Azure Certification"
                    className="w-10 h-10 object-contain mr-4"
                  />
                  <span className="text-gray-700 font-medium">Microsoft Azure AI Engineer</span>
                </div>
                
                <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/c06522e9d89b0dcc840760ba85d8656b.png"
                    alt="Google Cloud Certification"
                    className="w-10 h-10 object-contain mr-4"
                  />
                  <span className="text-gray-700 font-medium">Google Cloud Professional ML Engineer</span>
                </div>
                
                <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/c06522e9d89b0dcc840760ba85d8656b.png"
                    alt="ISO 27001 Certification"
                    className="w-10 h-10 object-contain mr-4"
                  />
                  <span className="text-gray-700 font-medium">ISO 27001 Information Security</span>
                </div>
                
                <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/c06522e9d89b0dcc840760ba85d8656b.png"
                    alt="GDPR Compliance Certification"
                    className="w-10 h-10 object-contain mr-4"
                  />
                  <span className="text-gray-700 font-medium">GDPR Compliance Specialist</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-black mb-6">Naši partneři</h3>
              <div className="grid grid-cols-2 gap-6 items-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-200 p-2">
                    <img 
                      src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/baaa575d3fc9a41536a9c042481a73fa.png"
                      alt="AurumAI Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-sm text-gray-700 font-medium">AurumAI</div>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-200 p-2">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Verum%20Capital%20professional%20financial%20company%20logo%2C%20modern%20corporate%20design%2C%20blue%20and%20white%20colors%2C%20clean%20minimalist%20style%2C%20investment%20fund%20branding%2C%20Czech%20Republic%20business%20logo%2C%20financial%20services%20design&width=120&height=120&seq=verum-capital-logo&orientation=squarish"
                      alt="Verum Capital Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-sm text-gray-700 font-medium">Verum Capital</div>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-200 p-2">
                    <img 
                      src="https://readdy.ai/api/search-image?query=All%20In%20Web%20professional%20web%20development%20company%20logo%2C%20modern%20technology%20design%2C%20blue%20and%20orange%20colors%2C%20web%20development%20branding%2C%20Czech%20Republic%20IT%20company%20logo%2C%20digital%20services%20design&width=120&height=120&seq=allinweb-logo&orientation=squarish"
                      alt="All In Web Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-sm text-gray-700 font-medium">All In Web</div>
                </div>
                
                <div className="text-center">
                  <div className="w-20 h-20 bg-white rounded-lg flex items-center justify-center mx-auto mb-3 border border-gray-200 p-2">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Bohemia%20Shield%20professional%20security%20company%20logo%2C%20shield%20symbol%2C%20dark%20blue%20and%20silver%20colors%2C%20protection%20and%20security%20branding%2C%20Czech%20Republic%20security%20services%20logo%2C%20professional%20corporate%20design&width=120&height=120&seq=bohemiashield-logo&orientation=squarish"
                      alt="Bohemia Shield Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-sm text-gray-700 font-medium">Bohemia Shield</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Kontaktujte nás
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Rádi si s vámi promluvíme o vašich potřebách a možnostech využití AI ve vaší firmě.
              </p>
              
              <div className="space-y-6">
                {/* Vedení */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Vedení</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 732 941 900</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">info@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Praha */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Praha</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 704 105 520</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">praha@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Brno */}
                <div>
                  <h3 className="text-lg font-semibold text-black mb-3">Brno</h3>
                  <div className="space-y-3 ml-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-phone-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="font-medium text-black">+420 775 993 829</div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3 border border-gray-200">
                        <i className="ri-mail-line text-black text-lg"></i>
                      </div>
                      <div>
                        <div className="text-gray-600">brno@czechai.io</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-4 border border-gray-200">
                    <i className="ri-time-line text-black text-xl"></i>
                  </div>
                  <div>
                    <div className="font-medium text-black">Online dostupnost</div>
                    <div className="text-gray-600">
                      Po-Pá: 8:00 - 17:00<br />
                      So-Ne: Na domluvě
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-white rounded-2xl overflow-hidden border border-gray-200">
                {/* Background image - celý roztažený na plnou výšku */}
                <div className="h-full min-h-96 relative">
                  <img 
                    src="https://static.readdy.ai/image/2e0b1e22c1b74afa84b035f494e9bd64/52489fbe8576fadace8e09aff53142ec.jfif"
                    alt="Online konzultace"
                    className="w-full h-full min-h-96 object-cover"
                  />
                  
                  {/* Dark overlay for better text readability */}
                  <div className="absolute inset-0 bg-black/40"></div>
                  
                  {/* Content overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <i className="ri-customer-service-2-line text-6xl text-white mb-4"></i>
                      <h3 className="text-xl font-bold text-white mb-2">Online konzultace</h3>
                      <p className="text-gray-200">Kontaktujte nás pro rychlou online konzultaci</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA sekce */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Začněte svou AI transformaci ještě dnes
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Získejte nezávaznou konzultaci a zjistěte, jak AI může pomoci vaší firmě
          </p>
          <Link to="/kontakt">
            <Button size="lg" variant="secondary" className="bg-black text-white hover:bg-gray-800 border-none mx-auto">
              <i className="ri-file-text-line mr-2"></i>
              Vyplnit formulář
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
